import {Platform} from 'react-native';
import DeviceInfo from 'react-native-device-info';

import config from '../config';

export interface UserAgentInfo {
  appName: string;
  appVersion: string;
  osName: string;
  osVersion: string;
  deviceModel: string;
  reactNativeVersion: string;
}

/**
 * Cheap pre-filter: true when this looks like Android mobile web.
 *
 * NOT authoritative for Google Pay eligibility (DIS-1515). DevTools device
 * emulation rewrites `navigator.userAgent` but typically leaves UA Client
 * Hints alone; either way, useGooglePayAvailability's isReadyToPay probe is
 * the source of truth. This helper only skips loading pay.js on obvious
 * desktop. SSR/native-safe: false unless Platform.OS === 'web'.
 */
export const isLikelyMobileWeb = (): boolean => {
  if (Platform.OS !== 'web') return false;
  if (typeof navigator === 'undefined') return false;

  // Prefer UA Client Hints — not rewritten by DevTools UA emulation the way
  // navigator.userAgent is.
  const uaData = (
    navigator as Navigator & {
      userAgentData?: {mobile?: boolean; platform?: string};
    }
  ).userAgentData;
  if (uaData?.mobile !== undefined) {
    return uaData.mobile === true && /android/i.test(uaData.platform ?? '');
  }

  // Fallback for browsers without UA-CH.
  if (!navigator.userAgent) return false;
  return /android/i.test(navigator.userAgent);
};

/**
 * Get device information for user agent
 */
export const getUserAgentInfo = async (): Promise<UserAgentInfo> => {
  // Handle web platform where DeviceInfo might not work as expected
  if (Platform.OS === 'web') {
    const appName = config.APP_NAME || 'MyApp';
    // Get version from webpack DefinePlugin (injected at build time)
    const appVersion = process.env.APP_VERSION || '0.0.0';
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';

    // Extract browser info from user agent string
    let browserInfo = 'Web Browser';
    if (userAgent.includes('Chrome')) browserInfo = 'Chrome';
    else if (userAgent.includes('Safari')) browserInfo = 'Safari';
    else if (userAgent.includes('Firefox')) browserInfo = 'Firefox';
    else if (userAgent.includes('Edge')) browserInfo = 'Edge';

    return {
      appName,
      appVersion,
      osName: 'Web',
      osVersion: Platform.Version ? String(Platform.Version) : '1.0',
      deviceModel: browserInfo,
      reactNativeVersion: '0.82.1',
    };
  }

  const [appVersion, systemVersion, deviceModel, brand] = await Promise.all([
    DeviceInfo.getVersion(),
    DeviceInfo.getSystemVersion(),
    DeviceInfo.getModel(),
    DeviceInfo.getBrand(),
  ]);

  const appName = config.APP_NAME || 'MyApp';
  const osName = Platform.OS === 'ios' ? 'iOS' : Platform.OS === 'android' ? 'Android' : 'Web';

  // Platform.constants doesn't exist on web, safely access it
  let reactNativeVersion = '0.82.1'; // default fallback
  if (Platform.constants && Platform.constants.reactNativeVersion) {
    const {major, minor, patch} = Platform.constants.reactNativeVersion;
    reactNativeVersion = `${major}.${minor}.${patch}`;
  }

  return {
    appName,
    appVersion,
    osName,
    osVersion: systemVersion,
    deviceModel: Platform.OS === 'ios' ? deviceModel : `${brand} ${deviceModel}`,
    reactNativeVersion,
  };
};

/**
 * Generate custom user agent string
 * Format: AppName/Version (OS Version; DeviceModel) ReactNative/Version
 * Example: MyApp/1.2.3 (iOS 17.0; iPhone15,2) ReactNative/0.73.2
 */
export const generateUserAgent = async (): Promise<string> => {
  const info = await getUserAgentInfo();

  return `${info.appName}/${info.appVersion} (${info.osName} ${info.osVersion}; ${info.deviceModel}) ReactNative/${info.reactNativeVersion}`;
};

/**
 * Get cached user agent or generate new one
 */
let cachedUserAgent: string | null = null;

export const getCachedUserAgent = async (): Promise<string> => {
  if (!cachedUserAgent) {
    cachedUserAgent = await generateUserAgent();
  }
  return cachedUserAgent;
};

/**
 * Clear cached user agent (useful for testing or when app updates)
 */
export const clearUserAgentCache = (): void => {
  cachedUserAgent = null;
};
